import { useEffect, useState, type ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/api/client';
import { useAuth } from '@/lib/auth';
import type { DashboardSummary } from '@/api/types';

interface NavItem {
  to: string;
  label: string;
  end?: boolean;
  badge?: 'exceptions';
}

const FULL_NAV: NavItem[] = [
  { to: '/', label: 'Dashboard', end: true },
  { to: '/recharges/new', label: 'New recharge' },
  { to: '/requests', label: 'Company requests' },
  { to: '/orders', label: 'Orders' },
  { to: '/exceptions', label: 'Exceptions', badge: 'exceptions' as const },
  { to: '/reservations', label: 'Reservations' },
  { to: '/catalogue', label: 'Catalogue' },
  { to: '/float', label: 'Float' },
  { to: '/merchants', label: 'Merchants' },
  { to: '/settlement', label: 'Settlement' },
  { to: '/accounting', label: 'Accounting' },
  { to: '/vendor-payouts', label: 'Vendor payouts' },
  { to: '/settings/platform', label: 'Settings' },
  { to: '/settings/users', label: 'Users' },
  { to: '/settings/audit', label: 'Audit' },
];

// A vendor's staff only need their selling screen and their own sales history — not the rest of
// the admin console, which belongs to platform staff.
const VENDOR_NAV: NavItem[] = [
  { to: '/', label: 'Sell', end: true },
  { to: '/orders', label: 'Orders' },
  { to: '/webhooks', label: 'Webhooks' },
];

// A company user pays for its own recharges and nothing else — no merchant picker, no other
// company's data, no platform-admin screens.
const COMPANY_NAV: NavItem[] = [
  { to: '/', label: 'Dashboard', end: true },
  { to: '/recharges/new', label: 'New recharge' },
  { to: '/numbers', label: 'Numbers' },
  { to: '/orders', label: 'Orders' },
  { to: '/webhooks', label: 'Webhooks' },
];

export function Shell({ children }: { children: ReactNode }) {
  const { session, signOut } = useAuth();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const NAV = session?.merchantAccountType === 'VENDOR' ? VENDOR_NAV
    : session?.merchantAccountType === 'COMPANY' ? COMPANY_NAV
    : FULL_NAV;

  // The off-canvas drawer (mobile only) should close itself once a link has actually navigated,
  // not stay open covering the page it was just used to reach.
  useEffect(() => setMenuOpen(false), [location.pathname]);

  // The exception count rides in the navigation so it is visible from every page — but only the
  // full console nav has an "Exceptions" item, so vendor/company users never need this call.
  const { data } = useQuery({
    queryKey: ['summary'],
    queryFn: () => api.get<DashboardSummary>('/api/admin/metrics/summary'),
    refetchInterval: 15_000,
    enabled: NAV === FULL_NAV,
  });
  const openCases = data?.exceptions.openCases ?? 0;

  // Vendor staff get a flat navbar (no sidebar) — just a couple of items, so a top nav reads
  // fine on both desktop and mobile without a hamburger/drawer.
  const isVendor = session?.merchantAccountType === 'VENDOR';

  if (isVendor) {
    return (
      <div className="flex min-h-screen flex-col">
        <header className="border-b border-ink-200 bg-white">
          <div className="flex items-center justify-between px-4 py-3">
            <div>
              <div className="text-sm font-semibold text-ink-900">Recharge</div>
              <div className="text-xs text-ink-500">HOT integration platform</div>
            </div>
            <div className="flex items-center gap-3 text-sm">
              <span className="hidden text-ink-700 sm:inline">{session?.fullName ?? session?.email}</span>
              <button type="button" className="btn-secondary" onClick={() => void signOut()}>
                Sign out
              </button>
            </div>
          </div>
          <nav className="flex gap-1 overflow-x-auto border-t border-ink-200 px-2 py-1">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  `shrink-0 rounded px-3 py-2 text-sm ${
                    isActive ? 'bg-ink-100 font-medium text-ink-900' : 'text-ink-600 hover:bg-ink-50'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
        </header>
        <main className="min-w-0 flex-1 p-4 sm:p-6">{children}</main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen">
      {/* Backdrop: mobile only, closes the drawer on tap outside it. */}
      {menuOpen && (
        <div className="fixed inset-0 z-30 bg-ink-950/40 lg:hidden" onClick={() => setMenuOpen(false)} />
      )}

      <aside
        className={`fixed inset-y-0 left-0 z-40 w-64 shrink-0 border-r border-ink-200 bg-white
                    transition-transform duration-200 lg:static lg:z-auto lg:w-52 lg:translate-x-0 ${
          menuOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex items-center justify-between border-b border-ink-200 px-4 py-4">
          <div>
            <div className="text-sm font-semibold text-ink-900">Recharge</div>
            <div className="text-xs text-ink-500">HOT integration platform</div>
          </div>
          <button type="button" className="rounded p-1 text-ink-500 hover:bg-ink-100 lg:hidden"
                  onClick={() => setMenuOpen(false)} aria-label="Close menu">
            ✕
          </button>
        </div>
        <nav className="p-2">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `flex items-center justify-between rounded px-3 py-2 text-sm ${
                  isActive ? 'bg-ink-100 font-medium text-ink-900' : 'text-ink-600 hover:bg-ink-50'
                }`
              }
            >
              <span>{item.label}</span>
              {item.badge === 'exceptions' && openCases > 0 && (
                <span className="rounded-full bg-red-600 px-1.5 text-[11px] font-semibold text-white">
                  {openCases}
                </span>
              )}
            </NavLink>
          ))}
        </nav>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex items-center justify-between gap-2 border-b border-ink-200 bg-white px-4 py-3 sm:px-6">
          <div className="flex min-w-0 items-center gap-3">
            <button type="button" className="shrink-0 rounded p-1 text-ink-600 hover:bg-ink-100 lg:hidden"
                    onClick={() => setMenuOpen(true)} aria-label="Open menu">
              <span className="block h-0.5 w-5 bg-current" />
              <span className="mt-1 block h-0.5 w-5 bg-current" />
              <span className="mt-1 block h-0.5 w-5 bg-current" />
            </button>
            <div className="hidden truncate text-xs text-ink-500 sm:block">
              All times Central Africa Time (UTC+2)
            </div>
          </div>
          <div className="flex min-w-0 items-center gap-2 text-sm sm:gap-3">
            <span className="hidden truncate text-ink-700 sm:inline">
              {session?.fullName ?? session?.email}
            </span>
            <span className="hidden rounded bg-ink-100 px-1.5 py-0.5 text-[11px] font-semibold text-ink-700 sm:inline">
              {session?.role}
            </span>
            <button type="button" className="btn-secondary shrink-0" onClick={() => void signOut()}>
              Sign out
            </button>
          </div>
        </header>
        <main className="min-w-0 flex-1 p-4 sm:p-6">{children}</main>
      </div>
    </div>
  );
}
